import React, { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ArrowLeft } from 'lucide-react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ProductCard } from '../components/ProductCard';
import { CTA } from '../components/CTA';
import { PRODUCTS } from '../components/products';
import type { Product } from '../components/products';
type GoalSlug = 'recovery' | 'weight-management' | 'performance' | 'skin-hair' | 'longevity' | 'sleep';
const GOAL_MAP: Record<GoalSlug, {
  goal: Product['goals'][number];
  title: string;
  intro: string;
}> = {
  recovery: {
    goal: 'Recovery',
    title: 'Recovery & healing peptides',
    intro: 'Researched for tissue repair, gut healing, and joint recovery. Includes the BPC and TB-500 families.'
  },
  'weight-management': {
    goal: 'Weight management',
    title: 'Weight management peptides',
    intro: 'GLP-1 and GIP receptor agonist research peptides — Semaglutide, Tirzepatide, and related compounds.'
  },
  performance: {
    goal: 'Performance',
    title: 'Performance research peptides',
    intro: 'GH-axis, strength, and endurance research. Tesamorelin, CJC-1295, Ipamorelin and related compounds.'
  },
  'skin-hair': {
    goal: 'Skin & Hair',
    title: 'Skin & hair research peptides',
    intro: 'Cosmetic and dermal research peptides — GHK-Cu copper tripeptide, melanotan family.'
  },
  longevity: {
    goal: 'Longevity',
    title: 'Longevity research peptides',
    intro: 'Cellular and anti-aging research compounds — NAD+ precursors, epitalon, and related peptides.'
  },
  sleep: {
    goal: 'Sleep',
    title: 'Sleep & stress research peptides',
    intro: 'Neurorecovery and stress-axis research compounds — DSIP, Selank, and related peptides.'
  }
};
export function Category() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const config = slug ? GOAL_MAP[slug as GoalSlug] : null;
  if (!config) {
    return <Navigate to="/shop" replace data-id="element-1239" />;
  }
  const items = useMemo(() => PRODUCTS.filter(p => p.goals.includes(config.goal)), [config.goal]);
  const otherCategories = (Object.entries(GOAL_MAP) as Array<[GoalSlug, (typeof GOAL_MAP)[GoalSlug]]>).filter(([s]) => s !== slug);
  return <div className="min-h-screen w-full bg-background" data-id="element-1240">
      <AnnouncementBar data-id="element-1241" />
      <Header data-id="element-1242" />

      <main data-id="element-1243">
        {/* Breadcrumb */}
        <div className="w-full border-b border-border" data-id="element-1244">
          <div className="container mx-auto py-5" data-id="element-1245">
            <nav className="flex items-center gap-1.5 text-[12px] text-muted-foreground" aria-label="Breadcrumb" data-id="element-1246">
              <Link to="/" className="hover:text-foreground transition-colors" data-id="element-1247">
                Home
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1248" />
              <Link to="/shop" className="hover:text-foreground transition-colors" data-id="element-1249">
                Shop
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1250" />
              <span className="text-foreground" data-id="element-1251">{config.goal}</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="container mx-auto pt-10 lg:pt-14 pb-10 lg:pb-12" data-id="element-1252">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="grid lg:grid-cols-[1.5fr_1fr] gap-8 items-end" data-id="element-1253">
            <div data-id="element-1254">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-1255">
                Category
              </p>
              <h1 className="text-3xl lg:text-[52px] font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-1256">
                {config.title}
                <span className="text-primary" data-id="element-1257">.</span>
              </h1>
              <p className="mt-5 text-[16px] leading-relaxed text-muted-foreground max-w-2xl" data-id="element-1258">
                {config.intro}
              </p>
            </div>
            <div className="flex lg:justify-end items-baseline gap-4 lg:gap-6 text-[12px] text-muted-foreground" data-id="element-1259">
              <div data-id="element-1260">
                <div className="font-mono text-[24px] font-semibold text-foreground leading-none [font-feature-settings:'tnum']" data-id="element-1261">
                  {items.length}
                </div>
                <div className="mt-1.5" data-id="element-1262">
                  {items.length === 1 ? 'peptide' : 'peptides'}
                </div>
              </div>
              <div data-id="element-1263">
                <div className="font-mono text-[24px] font-semibold text-foreground leading-none [font-feature-settings:'tnum']" data-id="element-1264">
                  {items.length > 0 ? `${(items.reduce((s, p) => s + p.purity, 0) / items.length).toFixed(1)}%` : '—'}
                </div>
                <div className="mt-1.5" data-id="element-1265">avg purity</div>
              </div>
              <div data-id="element-1266">
                <div className="font-mono text-[24px] font-semibold text-foreground leading-none [font-feature-settings:'tnum']" data-id="element-1267">
                  {items.length > 0 ? `£${Math.min(...items.map(p => p.price))}` : '—'}
                </div>
                <div className="mt-1.5" data-id="element-1268">from</div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Grid */}
        <section className="container mx-auto pb-20 lg:pb-28" data-id="element-1269">
          {items.length === 0 ? <div className="rounded-2xl border border-border bg-card p-10 text-center" data-id="element-1270">
              <p className="text-[15px] text-foreground font-medium" data-id="element-1271">
                No peptides in this category yet.
              </p>
              <p className="text-[13px] text-muted-foreground mt-2 mb-6" data-id="element-1272">
                Browse the full catalogue instead.
              </p>
              <CTA to="/shop" variant="primary" size="md" data-id="element-1273">
                View all peptides
              </CTA>
            </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-1274">
              {items.map(p => <ProductCard key={p.id} product={p} data-id="element-1275" />)}
            </div>}
        </section>

        {/* Other categories */}
        <section className="w-full bg-card border-y border-border" data-id="element-1276">
          <div className="container mx-auto py-16 lg:py-20" data-id="element-1277">
            <div className="flex items-end justify-between gap-6 mb-10" data-id="element-1278">
              <div data-id="element-1279">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-1280">
                  Other research areas
                </p>
                <h2 className="text-2xl lg:text-4xl font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-1281">
                  Browse another category<span className="text-primary" data-id="element-1282">.</span>
                </h2>
              </div>
              <CTA to="/shop" variant="secondary" size="md" icon={<ArrowLeft className="h-4 w-4" strokeWidth={2} data-id="element-1284" />} iconPosition="left" data-id="element-1283">
                All peptides
              </CTA>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-1285">
              {otherCategories.map(([s, cfg]) => {
              const count = PRODUCTS.filter(p => p.goals.includes(cfg.goal)).length;
              return <Link key={s} to={`/shop/category/${s}`} className="group flex items-center justify-between gap-3 px-5 py-4 rounded-xl bg-background border border-border hover:border-foreground/25 transition-colors" data-id="element-1286">
                    <div data-id="element-1287">
                      <div className="text-[15px] font-semibold tracking-tight text-foreground group-hover:text-primary transition-colors" data-id="element-1288">
                        {cfg.goal}
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground mt-0.5 [font-feature-settings:'tnum']" data-id="element-1289">
                        {count} {count === 1 ? 'peptide' : 'peptides'}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" strokeWidth={1.75} data-id="element-1290" />
                  </Link>;
            })}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1291" />
    </div>;
}